/**
 * The project overview list (docs/ux/artboards/app-clarity/overview, user decision 2026-10-08):
 * variant B (two lines per project) with variant A (a dense table) behind `t`. Pure model over
 * the overview tiles (lib/overview-model.ts) and the one needs-you count (lib/needs-you.ts).
 *
 * Line 2 is the latest meaningful progress: what waits on you, else a landing from the last day,
 * else the live line, else the last landing.
 *
 * Order: projects that are blocked or need you first (blocked before needs, most first), then the
 * active ones (most running first), and the quiet rest fold into one "N quiet projects" line.
 */
import type { OverviewTile } from './overview-model.js'

const DAY = 86_400_000

export type ProgressKind = 'run' | 'ok' | 'idle' | 'wait'

export interface ProjectRow {
  projectId: string
  name: string
  blocked: number
  /** Waiting on a decision (amber); excludes blocked. */
  needs: number
  running: number
  queued: number
  /** The first blocked item's reason, in words. */
  blockedReason?: string
  /** Line 2: the latest meaningful progress. */
  progress: { kind: ProgressKind; text: string; at?: number }
  /** Is the machine data behind this row current? */
  freshness: { stale: boolean; text: string }
}

export interface ProjectGroups {
  attention: ProjectRow[]
  active: ProjectRow[]
  quiet: ProjectRow[]
}

export function projectRow(
  tile: OverviewTile,
  counts: { blocked: number; needs: number },
  blockedReason?: string,
  now = Date.now(),
): ProjectRow {
  const live = tile.agents.find((a) => !a.waiting)
  const waiting = tile.agents.find((a) => a.waiting)
  // The latest *meaningful* progress: what waits on you, else a landing from the last day (a
  // verified outcome beats a live tool line), else the live line, else the last landing.
  const landed = tile.lastLanded
    ? ({ kind: 'ok', text: `Landed ${tile.lastLanded.title}`, at: tile.lastLanded.at } as const)
    : undefined
  const recent = landed && now - landed.at < DAY ? landed : undefined
  const progress: ProjectRow['progress'] = waiting
    ? { kind: 'wait', text: waitingText(waiting.line) }
    : (recent ??
      (live
        ? { kind: 'run', text: live.line }
        : (landed ?? {
            kind: 'idle',
            text: tile.queued ? `${tile.queued} queued` : 'No activity yet',
          })))
  return {
    projectId: tile.projectId,
    name: tile.name,
    blocked: counts.blocked,
    needs: counts.needs,
    running: tile.running,
    queued: tile.queued,
    ...(blockedReason ? { blockedReason } : {}),
    progress,
    freshness: tile.degradedReason
      ? { stale: true, text: tile.degradedReason }
      : { stale: false, text: '' },
  }
}

/** The lane line may already say "Waiting for you: …"; say it once. */
export function waitingText(line: string): string {
  return `Waiting for you: ${line.replace(/^(\s*Waiting for you:\s*)+/i, '')}`
}

/** The row's one status icon: blocked, needs you, running, else what its progress was. */
export function rowIcon(r: ProjectRow): ProgressKind | 'blocked' {
  if (r.blocked) return 'blocked'
  if (r.needs) return 'wait'
  if (r.running || r.progress.kind === 'run') return 'run'
  return r.progress.kind
}

/** One quiet project in the folded "Quiet" line: its name and how long it has been quiet. */
export function quietWhat(r: ProjectRow): { name: string; since?: number; text: string } {
  return r.progress.kind === 'ok' && r.progress.at
    ? { name: r.name, since: r.progress.at, text: 'last landed' }
    : { name: r.name, text: r.queued ? `${r.queued} queued` : 'idle' }
}

export function groupProjects(rows: readonly ProjectRow[]): ProjectGroups {
  const attention = rows
    .filter((r) => r.blocked > 0 || r.needs > 0)
    .sort(
      (a, b) =>
        Number(b.blocked > 0) - Number(a.blocked > 0) ||
        b.blocked - a.blocked ||
        b.needs - a.needs ||
        a.name.localeCompare(b.name),
    )
  const rest = rows.filter((r) => !(r.blocked > 0 || r.needs > 0))
  const active = rest
    .filter((r) => r.running > 0 || r.queued > 0 || r.progress.kind === 'run')
    .sort((a, b) => b.running - a.running || b.queued - a.queued || a.name.localeCompare(b.name))
  const quiet = rest.filter((r) => !active.includes(r)).sort((a, b) => a.name.localeCompare(b.name))
  return { attention, active, quiet }
}

export type ProjectDensity = 'rows' | 'table'
const KEY = 'viko.projects.density'

export function storedDensity(): ProjectDensity {
  try {
    return localStorage.getItem(KEY) === 'table' ? 'table' : 'rows'
  } catch {
    return 'rows'
  }
}
export function saveDensity(d: ProjectDensity) {
  try {
    localStorage.setItem(KEY, d)
  } catch {
    // private window: this session only
  }
}
